import { CookiesKey } from '@starsoft/common/enums';
import {
  dataParser,
  interceptorDataParser,
} from '@starsoft/common/utils/parsers';
import axios, { AxiosInstance } from 'axios';
import { Buffer } from 'buffer';
import { getCookie, hasCookie } from 'cookies-next';
import { NextRouter } from 'next/router';
import { Navigate as ReactRouter } from 'react-router-dom';
import { AuthService } from '../AuthService';
import { Either } from '../MoneyFormatter';

function getTokenExpirationSeconds(token: string): number {
  try {
    const payloadBase64 = token?.split?.('.')?.[1];
    const decodedPayload = JSON.parse(
      Buffer.from(payloadBase64, 'base64').toString(),
    );

    const expirationTimestamp = decodedPayload.exp;
    const nowInSeconds = Math.floor(Date.now() / 1000);
    const secondsRemaining = expirationTimestamp - nowInSeconds;

    return secondsRemaining;
  } catch (error) {
    console.dir(error, { depth: null });
    AuthService.setAsUnauthorized();
  }
}

export class APIClient {
  public instance: AxiosInstance;
  private tenantId: string;
  private navigation: Either<NextRouter, typeof ReactRouter>;
  private authTokenCookie: string;
  private refreshTokenCookie: string;

  constructor(
    baseURL: string,
    tenantId: string,
    navigation: Either<NextRouter, typeof ReactRouter>,
  ) {
    this.instance = axios.create({
      baseURL: baseURL + '/v1/',
      withCredentials: true,
      timeout: baseURL?.includes('admin') ? 40000 : 10000,
    });
    this.tenantId = tenantId;
    this.navigation = navigation;
    this.authTokenCookie = baseURL?.includes('admin')
      ? CookiesKey.TokenAdmin
      : CookiesKey.Token;
    this.refreshTokenCookie = baseURL?.includes('admin')
      ? CookiesKey.RefreshTokenAdmin
      : CookiesKey.RefreshToken;
    this.setupInterceptors();
  }

  private async axiosInterceptorRefreshToken() {
    let retries = 0;
    const maxRetries = 3;

    while (retries < maxRetries) {
      try {
        const response = await this.instance.post('iam/refresh');
        AuthService.setToken(response.data.accessToken, this.authTokenCookie);
        AuthService.setRefreshToken(
          response.data.refreshToken,
          this.refreshTokenCookie,
        );
        return;
      } catch (error) {
        retries++;
        if (retries === maxRetries) {
          AuthService.logOut();
          break;
        }
      }
    }
  }

  private setupInterceptors() {
    this.instance.interceptors.request.use(async request => {
      if (!request.headers) {
        return request;
      }

      const langCookie = getCookie(CookiesKey.Lang) as string;
      if (langCookie) {
        request.headers['x-lang'] = langCookie.slice(0, 2);
      }

      if (hasCookie(this.authTokenCookie)) {
        request.headers['authorization'] = getCookie(this.authTokenCookie);
        this.instance.defaults.headers['authorization'] = getCookie(
          this.authTokenCookie,
        ) as string;
      }

      if (hasCookie(this.refreshTokenCookie)) {
        request.headers['x-refresh-token'] = getCookie(this.refreshTokenCookie);
        this.instance.defaults.headers['x-refresh-token'] = getCookie(
          this.refreshTokenCookie,
        ) as string;
      }

      if (!!this.tenantId) {
        request.headers['x-tenant-id'] = this.tenantId;
      }

      return {
        ...request,
        data: dataParser(request.data),
      };
    });

    this.instance.interceptors.response.use(
      async response => {
        AuthService.refreshAuthorization(
          this.authTokenCookie,
          this.refreshTokenCookie,
        );

        if (response?.data?.accessToken) {
          AuthService.setToken(response.data.accessToken, this.authTokenCookie);
          this.instance.defaults.headers['authorization'] =
            response.data.accessToken;
          response.headers['authorization'] = response.data.accessToken;
        }

        if (response?.data?.refreshToken) {
          AuthService.setRefreshToken(
            response.data.refreshToken,
            this.refreshTokenCookie,
          );
          this.instance.defaults.headers['x-refresh-token'] =
            response.data.refreshToken;
          response.headers['x-refresh-token'] = response.data.refreshToken;
        }

        if (!!this.tenantId) {
          response.headers['x-tenant-id'] = this.tenantId;
        }

        const refreshToken =
          this.instance.defaults.headers['x-refresh-token'] ??
          response.headers['x-refresh-token'];

        if (typeof refreshToken === 'string' && typeof window !== 'undefined') {
          const tokenExpiration = getTokenExpirationSeconds(refreshToken);

          if (
            tokenExpiration &&
            tokenExpiration < 60 &&
            tokenExpiration > 0 &&
            hasCookie(this.refreshTokenCookie)
          ) {
            await this.axiosInterceptorRefreshToken();
          }

          if (tokenExpiration && tokenExpiration < 0) {
            AuthService.logOut();
          }
        }

        return {
          ...response,
          data: interceptorDataParser(response.data),
        };
      },
      async error => {
        const method: string = error.response?.config?.method ?? '';

        if (
          (error?.response?.data?.statusCode === 401 ||
            error?.response?.data?.statusCode === 403) &&
          method.toUpperCase() === 'GET'
        ) {
          AuthService.logOut();

          // Check if navigation is an instance of NextRouter
          if (
            'push' in this.navigation &&
            typeof this.navigation.push === 'function'
          ) {
            this.navigation.push('/');
            this.navigation.prefetch('/');
            return;
          }

          // Check if navigation is an instance of ReactRouter
          if (
            'navigate' in this.navigation &&
            typeof this.navigation.navigate === 'function'
          ) {
            this.navigation.navigate('/login');
            return;
          }

          // Fallback if neither
          window.open('/login', '_self');
        }

        return Promise.reject(error);
      },
    );
  }
}
